'use client'
import { type FC, Fragment, memo, type RefObject } from 'react'
import styled, { css } from 'styled-components'
import type { TypeCmsContentsBlock } from '@/schema'
import { ContentText } from './commons/contents/ContentText'
import { ContentMedia } from './commons/contents/ContentMedia'
import { ContentSlider } from './commons/contents/ContentSlider'
import { ContentUserJourney } from './commons/contents/ContentUserJourney'
import { getP12, getP18, getP24, getP50, getP90, mq, vw } from '@/styles'

type Props = {
  data: TypeCmsContentsBlock[]
  ref?: RefObject<HTMLElement>
}

const checkIfOnlyTextContents = (columns) => {
  let onlyTextContent = true
  for (const col of columns) {
    if (col.type !== 'text') onlyTextContent = false
  }

  return onlyTextContent
}

const checkIfTextWithImageContents = (columns) => {
  if (columns.length < 2) return false

  let textContent = false
  let imageContent = false

  for (const col of columns) {
    if (col.type === 'text') textContent = true
    if (col.type === 'media') imageContent = true
  }

  return (textContent && imageContent)
}

const contentByType = {
  text: (data) => <ContentText data={data} />,
  media: (data, contentsPerRow) => <ContentMedia data={data} contentsPerRow={contentsPerRow} />,
  slideshow: (data) => <ContentSlider data={data} />,
  empty: () => (<div className='empty'></div>),
  spacer: () => (<div className='spacer'></div>)
}

const getRowItems = (data: Props['data']) => data.map((item, idx) => {
  const onlyTextContents = checkIfOnlyTextContents(item.columns)
  const textWithImageContents = checkIfTextWithImageContents(item.columns)
  const contentsPerRow = item.columns?.length ?? 1
  const isSlider = item.columns?.[0]?.type === 'slideshow'
  const isMediaFullScreen = item.columns?.[0]?.type === 'media' && item.columns?.[0]?.fullscreen

	// if(item.content_type === 'three_columns') debugger
	// if(item.content_type === 'four_columns') debugger

	// console.log(`SectionContents id: ${item.id} type: ${item.content_type}`)

  return (
    <DivRow data-row-id={item.id} key={idx} className={`row${(isSlider || isMediaFullScreen) ? ' fullscreen' : ''}`} $contentsPerRow={contentsPerRow} $onlyTextContents={onlyTextContents} $textWithImageContents={textWithImageContents} data-row-type={item.content_type}>
			{item.content_type === 'user_journey' ? <ContentUserJourney /> :
			  <>
				{item.columns.map((column, index) => {
					return (
						<Fragment key={index}>
							{contentByType[column.type](column, contentsPerRow)}
						</Fragment>
					)
				})}
				</>
			}
    </DivRow>
  )
})

const headingId = 'contents'
export const SectionContents: FC<Props> = memo(({ data, ref }) => {
  const rowItems = getRowItems(data)

  return (
    <Section id='s--contents' {...ref && { ref }} aria-labelledby={headingId}>
      <h2 id={headingId} className='srt-only'>Info</h2>
      {rowItems}
    </Section>
  )
})

const Section = styled.section`
  padding: 90px 0 100px;
  width: 100vw;
  
  ${mq.greaterThan('desktop')} {
    padding: 110px 0 100px;
  }

  h1, h2 {
    ${getP90()}
  }

  h3 {
    ${getP50()}
  }

  h4 {
    ${getP18()}
  }

  h5, h6 {
    ${getP12()}
  }

  p, ul, ol, dl {
    ${getP24()}
  }

  strong {
    opacity: .4;
    font-weight: inherit;
  }
  
  &.spacer {
    height: ${vw(60, 'mobile')};
    margin-bottom: 0;
    padding-bottom: 0;

    ${mq.greaterThan('nexus7')} {
      height: ${vw(60, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      height: ${vw(120, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      height: 120px;
    }
  }
`

const DivRow = styled.div<{ $contentsPerRow: number, $onlyTextContents: boolean, $textWithImageContents: boolean }>`
  align-items: flex-start;
  column-gap: ${vw(23, 'mobile')};
  display: grid;
  flex-wrap: wrap;
  grid: auto-flow / repeat(6, 1fr);
  margin-bottom: ${({ $onlyTextContents }) => vw($onlyTextContents ? 60 : 100, 'mobile')};
  padding: ${vw(23, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    column-gap: ${vw(23, 'tablet')};
    margin-bottom: ${({ $onlyTextContents }) => vw($onlyTextContents ? 60 : 100, 'tablet')};
    padding: ${vw(23, 'tablet')};
  }

  ${mq.greaterThan('tablet')} {
    align-items: ${({ $onlyTextContents }) => $onlyTextContents ? 'flex-start' : 'center'};
    column-gap: ${vw(25, 'desktop')};
    grid: auto-flow / repeat(12, 1fr);
    margin-bottom: ${({ $onlyTextContents }) => vw($onlyTextContents ? 90 : 130, 'desktop')};
    padding: 0 ${vw(25, 'desktop')};
    position: relative;
  }

  ${mq.greaterThan('desktop')} {
    column-gap: 25px;
    margin-bottom: ${({ $onlyTextContents }) => $onlyTextContents ? 90 : 130}px;
    padding: 0 25px;
  }

  &.fullscreen {
    padding: 0 !important;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }
  }

  .ct {
    display: flex;

    ${mq.lessThan('tablet')} {
      grid-column: 1 / span 6;
      justify-content: center;
    }

    &.ct--text {
      ${({ $contentsPerRow, $onlyTextContents }) => $contentsPerRow === 3 && $onlyTextContents && css`
        ${mq.lessThan('tablet')} {
          grid-column: 1 / span 4;
        }
      `}
    }

    &.ct--media {
      ${({ $textWithImageContents }) => $textWithImageContents && css`
        ${mq.lessThan('tablet')} {
          grid-column: 1 / span 6 !important;
          margin-bottom: inherit !important;

          .media {
            width: ${vw(172, 'tablet')};
          }
        }

        ${mq.lessThan('nexus7')} {
          .media {
            width: ${vw(172, 'mobile')};
          }
        }
      `}
    }

    &:not(:last-of-type) {
      margin-bottom: ${({ $contentsPerRow }) => vw($contentsPerRow === 2 ? 20 : 30, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        margin-bottom: ${({ $contentsPerRow }) => vw($contentsPerRow === 2 ? 20 : 30, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        margin-bottom: 0;
      }
    }
  }


	&[data-row-type='two_columns'] {
		> div.ct.ct--text {
			${mq.greaterThan('tablet')} {
				grid-column-end: span 5 !important;
			}
		}
	}

	&[data-row-type='three_columns'] {
		grid: auto-flow/repeat(5, 1fr) !important;

		${mq.greaterThan('tablet')} {
			grid: auto-flow/repeat(13, 1fr) !important;
		}

		> .spectral-echoes {
			${mq.lessThan('tablet')} {
				height: auto;
			}
		}

		> div {
			grid-column: 2 / span 3 !important;

			&:first-child {
				${mq.greaterThan('tablet')} {
					grid-column: 2 / span 3 !important;
				}
			}

			&:nth-child(2) {
				${mq.greaterThan('tablet')} {
					grid-column: 6 / span 3 !important;
				}
			}

			&:last-child {	
				${mq.greaterThan('tablet')} {
					grid-column: 10 / span 3 !important;
				}
			}
		}

		figure,
		img {
			aspect-ratio: 165 / 247;
			height: auto;
			max-height: 100%;
			width: 100%;
		}
	}

	&[data-row-type='four_columns'] {
		> div {
			grid-column: auto / span 3 !important;
			height: fit-content !important;
			height: ${vw(150, 'mobile')} !important;
			margin-bottom: 75px !important;

			${mq.greaterThan('nexus7')} {
				height: ${vw(150, 'tablet')} !important;
			}

			${mq.greaterThan('tablet')} {
				height: ${vw(221, 'desktop')} !important;
				margin-bottom: 0 !important;
			}

			${mq.greaterThan('desktop')} {
				height: 221px !important;
			}

			figure,
			img {
				aspect-ratio: 320 / 221;
				flex-shrink: 0;
			}

			figure,
			img,
			.wrapper-hover-text {
				height: ${vw(150, 'mobile')} !important;

				${mq.greaterThan('nexus7')} {
					height: ${vw(150, 'tablet')} !important;
				}

				${mq.greaterThan('tablet')} {
					height: ${vw(221, 'desktop')} !important;
				}

				${mq.greaterThan('desktop')} {
					height: 221px !important;
				}
			}
		}
	}
`
